import { buildLogoutUrl } from '@core/login-url';
import { CLAIM_AUTHORIZED_GROUP } from '@core/types';
import type { AppView } from '../api/client';
import { issuerUrl, revokeEndpoint, type RuntimeConfig } from '../auth/oauth';

/**
 * The text an operator hands to whoever is building the app - increasingly an LLM builder like
 * Replit rather than a person.
 *
 * Two sections, because they answer different questions. Configuration is what to paste into a
 * client; Rules are what to do that the configuration does not imply. A builder that already knows
 * OAuth needs the second list far more than the first.
 *
 * The issuer leads and the authorize and token endpoints are deliberately absent: Cognito's
 * discovery document already advertises them, pointed at this deployment's custom domain, so
 * restating them here would be a second description of the flow free to drift from the one clients
 * actually read. Only the revoke endpoint appears, because a rule instructs a POST to it directly.
 *
 * The rules are imperative rather than explanatory. Where the discovery document is silent about
 * PKCE and about public-client token auth, what a builder needs is "force it and override the
 * library if it objects", not a description of which metadata field is missing.
 *
 * Markdown, because the destination is usually a chat box.
 *
 * Every value is read from the app record or the runtime config, never hardcoded.
 */
export function buildImplementationPrompt(app: AppView, config: RuntimeConfig): string {
  const lines: string[] = [
    'Add single sign-on using OpenID Connect (Authorization Code Flow + PKCE) to this app.',
    '',
    '**OIDC Configuration**',
    // Discovery is the reason no other endpoint is listed, so it is said once, here.
    `* **Issuer:** \`${issuerUrl(config)}\` (supports OIDC discovery, so most libraries need only this)`,
    `* **Client ID:** \`${app.client_id}\``,
    '* **Client Secret:** None (public client).',
    // Space-joined, because that is the literal value of the scope parameter.
    `* **Scopes:** \`${config.scopes.join(' ')}\``,
  ];

  // Byte-exact. Cognito string-matches redirect_uri and normalization appends a trailing slash to
  // a bare origin, so a rebuilt value fails with redirect_mismatch and no further explanation.
  // Short, but it stays: it is the single most common way this integration fails.
  if (app.redirect_uris.length === 1) {
    lines.push(
      `* **Redirect URI:** \`${app.redirect_uris[0]}\` - must match exactly, including any trailing slash.`,
    );
  } else if (app.redirect_uris.length > 1) {
    lines.push('* **Redirect URIs:** use the one matching the environment, exactly as written:');
    lines.push(...app.redirect_uris.map((uri) => `    * \`${uri}\``));
  }

  // Byte-exact for the same reason the redirect URI is, and listed separately because it is the
  // input to a different endpoint. Only the first is described: /logout takes one logout_uri, and a
  // prompt offering a choice invites the builder to send an unregistered variation of it.
  const logoutUri = app.logout_uris?.[0];
  if (logoutUri) {
    lines.push(`* **Logout URI:** \`${logoutUri}\` - must match exactly, as above.`);
  }

  const rules: string[] = [
    // One instruction covering two silences in the metadata: no code_challenge_methods_supported,
    // and no `none` under token_endpoint_auth_methods_supported. A builder needs the override, not
    // an account of which field is missing.
    '**PKCE and public-client overrides:** Force PKCE `S256` explicitly and configure the token endpoint for public authentication with no client secret. The discovery document advertises neither, so bypass the library checks if necessary.',
    `**Custom authorize parameter:** Always include \`identity_provider=${config.idpName}\` on the authorization query string, so users go straight to Microsoft instead of a provider chooser.`,
    '**User profile:** Identify the signed-in user by the ID token\'s `email` claim.',
    // Kept against the trimmed version. This claim reads like authorization and is not: the trigger
    // writes the literal "none" when no check ran, so an app treating its presence as proof would
    // admit every user in the tenant. That turns a gated app into an open one silently.
    `**Do not use \`${CLAIM_AUTHORIZED_GROUP}\` for authorization.** The ID token carries it, but the literal value \`none\` means no group check was performed - reading it as proof of membership would admit everyone in the tenant.`,
    // Stated because builders have shipped without it: the refresh token was taken from the token
    // response and dropped, leaving a session that died at the access token's expiry and sent the
    // user back through Entra every hour. No lifetimes are named - they are Cognito defaults this
    // deployment does not set, so a number here would be free to drift.
    '**Token refresh:** Keep the refresh token from the token response and use it to renew the session. Do not discard it.',
    // Two-step, and the order matters: revoke first, because the second step navigates away and a
    // fetch in flight during that navigation is not guaranteed to complete.
    logoutUri
      ? `**Sign-out:** POST to \`${revokeEndpoint(config)}\` with \`token=<refresh_token>\` and \`client_id=${app.client_id}\`, clear local sessions and tokens, then redirect the browser to \`${buildLogoutUrl({ domainName: config.domainName, clientId: app.client_id, logoutUri })}\`. That last step is what ends the broker session itself - without it the next sign-in succeeds with no password prompt. Note this endpoint is \`/logout\` with a \`logout_uri\` parameter, not the OIDC \`end_session_endpoint\`, so build the URL literally rather than from discovery.`
      : // No logout URI is registered, so /logout would answer redirect_mismatch. Say what is
        // missing rather than describing an endpoint this client cannot use.
        `**Sign-out:** POST to \`${revokeEndpoint(config)}\` with \`token=<refresh_token>\` and \`client_id=${app.client_id}\`, then clear local sessions and tokens. This ends the app's session but not the broker's, so signing back in will not prompt for a password - ask the broker operator to register a logout URI for this app if that matters.`,
  ];

  // Only for a gated app. Where any tenant user may sign in, a failed callback is a fault rather
  // than a refusal, and telling the builder otherwise would have it swallow real errors.
  if (app.required_group_ids.length > 0) {
    rules.push(
      '**Error handling:** A failed callback means the user is not authorized, since access is restricted to specific Entra groups. Show a clean "Not Authorized" message rather than crashing.',
    );
  }

  lines.push('', '**Critical Authentication Rules**');
  lines.push(...rules.map((rule, i) => `${i + 1}. ${rule}`));

  return lines.join('\n');
}
